import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Table, Form, FloatingLabel, OverlayTrigger, Tooltip, Dropdown, Spinner, Modal, Button, Alert } from 'react-bootstrap'
import useTableData from '../hooks/useTableData'
import Notification from '../components/Notification'
import { apiClient } from "../services/api/client"
import { fetchObjednavky, deleteObjednavka, checkObjednavkaDeletion, createSkladovyPohyb } from "../services/api/objednavky"
import OrdersPrintTrigger from '../components/OrdersPrintTrigger'
import OrdersSalePrintTrigger from '../components/OrdersSalePrintTrigger'
import OrdersDeliveryPrintTrigger from '../components/OrdersDeliveryPrintTrigger'
import { useAutoRefresh } from '../hooks/useAutoRefresh'

const ORDERS_FILTER_STORAGE_KEY = 'orders_active_filters_v1'

const loadStoredOrdersFilterState = () => {
  const defaults = {
    filtr: {},
    dateInput: '',
    priceInput: '',
    cisloInput: '',
    icoInput: '',
    zakaznikInput: '',
    spzInput: '',
    cisloPohInput: '',
    origcisloInput: '',
    zakazkaInput: '',
  }
  try {
    if (typeof window === 'undefined') return defaults
    const raw = window.localStorage.getItem(ORDERS_FILTER_STORAGE_KEY)
    if (!raw) return defaults
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return defaults
    const parsedFiltr = parsed.filtr && typeof parsed.filtr === 'object' ? { ...parsed.filtr } : {}
    delete parsedFiltr.stredisko
    delete parsedFiltr.stredisko_prefix
    return {
      filtr: parsedFiltr,
      dateInput: String(parsed.dateInput ?? ''),
      priceInput: String(parsed.priceInput ?? ''),
      cisloInput: String(parsed.cisloInput ?? ''),
      icoInput: String(parsed.icoInput ?? ''),
      zakaznikInput: String(parsed.zakaznikInput ?? ''),
      spzInput: String(parsed.spzInput ?? ''),
      cisloPohInput: String(parsed.cisloPohInput ?? ''),
      origcisloInput: String(parsed.origcisloInput ?? ''),
      zakazkaInput: String(parsed.zakazkaInput ?? ''),
    }
  } catch {
    return defaults
  }
}

const Orders = () => {  
  const storedFilterState = loadStoredOrdersFilterState()
  const [objednavky, setObjednavky] = useState([])
  const [strediska, setStrediska] = useState([])
  const [uzivatele, setUzivatele] = useState([])  // dynamicky z objednávek
  
  const [isFiltrOpen, setIsFiltrOpen] = useState(false)
  const [filtr, setFiltr] = useState(storedFilterState.filtr)

  const [dateInput, setDateInput] = useState(storedFilterState.dateInput)
  const [isDateInvalid, setIsDateInvalid] = useState(false)
  const [priceInput, setPriceInput] = useState(storedFilterState.priceInput)
  const [isPriceInvalid, setIsPriceInvalid] = useState(false)
  const [cisloInput, setCisloInput] = useState(storedFilterState.cisloInput)
  const [icoInput, setIcoInput] = useState(storedFilterState.icoInput)
  const [zakaznikInput, setZakaznikInput] = useState(storedFilterState.zakaznikInput)
  const [spzInput, setSpzInput] = useState(storedFilterState.spzInput)
  const [cisloPohInput, setCisloPohInput] = useState(storedFilterState.cisloPohInput)
  const [origcisloInput, setOrigcisloInput] = useState(storedFilterState.origcisloInput)
  const [zakazkaInput, setZakazkaInput] = useState(storedFilterState.zakazkaInput)

  const [notifications, setNotifications] = useState([])  
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isStockMoveModalOpen, setIsStockMoveModalOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [deleteCheck, setDeleteCheck] = useState(null)
  const [isDeleteCheckLoading, setIsDeleteCheckLoading] = useState(false)
  const [stockMoveType, setStockMoveType] = useState('cash')
  const [isCreatingStockMove, setIsCreatingStockMove] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('')
  const [openActionsDropdownId, setOpenActionsDropdownId] = useState(null)

  const navigate = useNavigate()
  const location = useLocation()

  const ORDER_STATUSES = [
    { code: 2, subcode: 'prijato', popis: 'přijato', icon: 'bi-clipboard-check', color: 'text-muted', bgColor: 'rgba(var(--bs-secondary-rgb), 0.03)', sort: 2.0 },
    { code: 2, subcode: 'dilna', popis: 'dílna', icon: 'bi-wrench', color: 'text-primary', bgColor: 'rgba(var(--bs-primary-rgb), 0.08)', sort: 2.1 },
    { code: 2, subcode: 'provedeno', popis: 'provedeno', icon: 'bi-hourglass-split', color: 'text-danger', bgColor: 'rgba(var(--bs-danger-rgb), 0.08)', sort: 2.2 },
    { code: 1, subcode: null, popis: 'částečně vyřízeno', icon: 'bi-check-circle', color: 'text-warning', bgColor: 'rgba(var(--bs-warning-rgb), 0.15)', sort: 1.0 },
    { code: 0, subcode: null, popis: 'vyřízeno', icon: 'bi-check-circle', color: 'text-success', bgColor: 'rgba(var(--bs-success-rgb), 0.12)', sort: 0.0 },
  ]

  const normalizeSubcode = (value) => {
    return (value ?? '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '')
  }

  const getOrderStatus = (code, subcodeRaw) => {
    if (Number(code) !== 2) {
      return ORDER_STATUSES.find((s) => s.code === Number(code) && s.subcode === null)
    }
    const subcode = normalizeSubcode(subcodeRaw)
    return (
      ORDER_STATUSES.find((s) => s.code === 2 && s.subcode === subcode) ||
      ORDER_STATUSES.find((s) => s.code === 2 && s.subcode === 'prijato')
    )
  }

  // Načtení stránky
  useEffect(() => {
    fetchStrediska()  
  }, [])

  useEffect(() => {
    const type = location.state?.type
    if (!type) return
    let message = ''
    if (type === 'create') message = 'Objednávka byla uložena.'
    if (type === 'update') message = 'Objednávka byla upravena.'
    if (!message) {
      navigate(location.pathname, { replace: true, state: null })
      return
    }
    setNotifications((prev) => {
      if (prev.some((n) => n.message === message)) return prev
      return [
        ...prev,
        {
          id: Date.now(),
          message,
          time: new Date().toLocaleTimeString(),
        }
      ]
    })
    navigate(location.pathname, { replace: true, state: null })
  }, [location.state, location.pathname, navigate])

  useEffect(() => {
    if (strediska.length) fetchData()
  }, [strediska])

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return
      const hasFilters = filtr && Object.keys(filtr).length > 0
      const hasInputs = [dateInput, priceInput, cisloInput, icoInput, zakaznikInput, spzInput, cisloPohInput, origcisloInput, zakazkaInput].some(
        (v) => String(v || '').trim() !== ''
      )
      if (!hasFilters && !hasInputs) {
        window.localStorage.removeItem(ORDERS_FILTER_STORAGE_KEY)
        return
      }
      window.localStorage.setItem(
        ORDERS_FILTER_STORAGE_KEY,
        JSON.stringify({
          filtr,
          dateInput,
          priceInput,
          cisloInput,
          icoInput,
          zakaznikInput,
          spzInput,
          cisloPohInput,
          origcisloInput,
          zakazkaInput,
        })
      )
    } catch {
      // ignore localStorage errors
    }
  }, [filtr, dateInput, priceInput, cisloInput, icoInput, zakaznikInput, spzInput, cisloPohInput, origcisloInput, zakazkaInput])

  /*useEffect(() => {
    console.log('filtr:',filtr)
  }, [filtr])*/

  // Načtení objednávek (Firebird)
  const fetchData = async () => {
    try {
      setIsLoading(true)
      // prefixy bereme z MySQL středisek (nejspolehlivější klíč)
      const prefixes =
        (strediska || [])
          .map((s) => (s.prefix_mrp || "").trim().toUpperCase())
          .filter(Boolean)
      const list = await fetchObjednavky({
        prefixes: prefixes,
        limit: 1000,
      })
      const mapped = (list || []).map((o) => {
        const cislo = (o.CISLO || "").trim()
        const prefix = cislo.slice(0, 5)        // Z2604        
        const st = (strediska || []).find(      // název střediska z MySQL dle prefixu
          (s) => String(s.prefix_mrp || "").trim().toUpperCase() === prefix
        )
        const strediskoNazev = st ? `${st.cislo} ${st.nazev}` : prefix
        const stavMrp = Number(o.VYBAVENE)
        const stavApp = (o.STAV || '').trim()
        const finalStatus = getOrderStatus(stavMrp, stavApp)
        return {
          idobj: o.IDOBJ,
          vytvoreno: o.DATUM,
          cislo, 
          ico: (o.ICO || "").trim(),
          dic: (o.DIC || "").trim(),
          zakaznik: (o.FIRMA || "").trim(),          
          stredisko_prefix: prefix,               // pro řazení/filtrování: prefix je klíč
          stredisko: strediskoNazev,              // pro zobrazení: název (fallback prefix)
          spz: o.SPZ,
          stani: o.STANI,
          stavApp,                                // sub-stav z MRP (přijato/dílna/provedeno)
          origcislo: (o.ORIGCISLO || "").trim(),
          cisloPoh: (o.CISLOPOH || "").trim(),
          zakazka: String(o.ZAKAZKA ?? '').trim().replace(/^0$/, ''),
          celkem: Number(o.CELKOM || 0),
          zaklad: Number(o.ZAKLAD || 0),
          uzivatel: (o.LOG_USER || "").trim(),    // uživatel (text)
          stavMrp,                                // MRP stavy 0/1/2
          stav: finalStatus?.code ?? stavMrp,     // pro filtrování (useTableData)
          stav_key: `${finalStatus?.code ?? stavMrp}|${finalStatus?.subcode ?? ''}`,
          stav_label: finalStatus?.popis || '',
          stav_icon: finalStatus?.icon || '',
          stav_color: finalStatus?.color || '',
          stav_bg: finalStatus?.bgColor || '',
          stav_sort: finalStatus?.sort ?? stavMrp,
        }
      })
      setObjednavky(mapped)
      // dynamický select uživatelů
      const uniqueUsers = [...new Set(mapped.map((x) => x.uzivatel).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "cs")
      )
      setUzivatele(uniqueUsers.map((name) => ({ id: name, jmeno: name })))
    } catch (error) {
      console.error("Chyba při načítání objednávek:", error)
    } finally {
      setIsLoading(false)
      setHasLoaded(true)
    }
  }

  useAutoRefresh(fetchData, {
    enabled: strediska.length > 0,
    shouldRun: () => !isDeleteModalOpen && !isStockMoveModalOpen && !isLoading && !isDeleting && !isCreatingStockMove,
    deps: [strediska.length, isDeleteModalOpen, isStockMoveModalOpen, isLoading, isDeleting, isCreatingStockMove],
  })
  
  // Načtení středisek
  const fetchStrediska = async () => {
    try {
      const list = await apiClient.get('/strediska')
      setStrediska(Array.isArray(list) ? list : [])
    } catch (error) {
      console.error('Chyba při načítání dat:', error)
      setStrediska([])
    }
  }
  
  // Props pro hook řazení a stránkování
  const table = useTableData(objednavky, {
    defaultSortField: 'vytvoreno',
    defaultSortDirection: 'desc',
    searchableFields: ['vytvoreno', 'cislo', 'cisloPoh', 'origcislo', 'zakazka', 'ico', 'zakaznik', 'stav_label', 'spz', 'celkem', 'uzivatel'],
    filteredFields: filtr
  })

  // Nová objednávka
  const handleCreate = () => {
    navigate('/ordercreate')
  }

  // Editace objednávky
  const handleUpdate = (order) => {
    navigate('/orderupdate', {
      state: {
        idobj: order?.idobj,
        orderStatus: {
          label: order?.stav_label || '',
          icon: order?.stav_icon || '',
          color: order?.stav_color || '',
          bg: order?.stav_bg || ''
        }
      }
    })
  }

  const toNum = (value) => {
    if (value === null || value === undefined) return 0
    const n = Number(String(value).replace(',', '.').trim())
    return Number.isFinite(n) ? n : 0
  }
  const toTrim = (value) => String(value ?? '').trim()
  const handleOrderPrintError = (error) => {
    setNotifications((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: 'error',
        message: error?.message || 'Tisk objednavky se nepodaril.',
        time: new Date().toLocaleTimeString(),
      }
    ])
  }

  const closeParentDropdown = (target) => {
    const menu = target?.closest?.('.dropdown-menu')
    const toggle = menu?.previousElementSibling
    if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
      toggle.click()
    }
  }

  const isInvoiceOnlyByLaw = (order) => {
    const dic = String(order?.dic || '').trim().toUpperCase().replace(/\s+/g, '')
    const zaklad = toNum(order?.zaklad)
    return dic.startsWith('CZ') && zaklad > 10000
  }

  // Smazani objednavky
  const openDeleteModal = async (order) => {
    setSelectedOrder(order)
    setDeleteCheck(null)
    setDeleteErrorMessage('')
    setIsDeleteModalOpen(true)
    setIsDeleteCheckLoading(true)
    try {
      const result = await checkObjednavkaDeletion(order?.idobj)
      setDeleteCheck(result)
    } catch (error) {
      setDeleteErrorMessage(error?.message || 'Vazby objednávky se nepodařilo ověřit.')
    } finally {
      setIsDeleteCheckLoading(false)
    }
  }

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setSelectedOrder(null)
    setIsDeleting(false)
    setDeleteCheck(null)
    setIsDeleteCheckLoading(false)
    setDeleteErrorMessage('')
  }

  const openStockMoveModal = (order) => {
    setSelectedOrder(order)
    setStockMoveType(isInvoiceOnlyByLaw(order) ? 'invoice' : 'cash')
    setIsStockMoveModalOpen(true)
  }

  const closeStockMoveModal = () => {
    setIsStockMoveModalOpen(false)
    setSelectedOrder(null)
    setStockMoveType('cash')
    setIsCreatingStockMove(false)
  }

  const handleCreateStockMove = async () => {
    const idobj = Number(selectedOrder?.idobj)
    if (!Number.isFinite(idobj) || idobj <= 0 || isCreatingStockMove) return
    if (isInvoiceOnlyByLaw(selectedOrder) && stockMoveType === 'cash') {
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'error',
          message: 'Pro tuto objednávku je možné vytvořit pouze výdej na fakturu.',
          time: new Date().toLocaleTimeString(),
        }
      ])
      return
    }
    const druhpohybu = stockMoveType === 'invoice' ? 1 : 2
    try {
      setIsCreatingStockMove(true)
      const result = await createSkladovyPohyb({ idobj, druhpohybu })
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now(),
          message: `Skladový pohyb ${result?.cisloPoh || ''} byl vytvořen.`,
          time: new Date().toLocaleTimeString(),
        }
      ])
      closeStockMoveModal()
      await fetchData()
    } catch (error) {
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'error',
          message: error?.message || 'Skladový pohyb se nepodařilo vytvořit.',
          time: new Date().toLocaleTimeString(),
        }
      ])
      setIsCreatingStockMove(false)
    }
  }

  const handleDeleteOrder = async (e) => {
    e.preventDefault()
    if (!selectedOrder?.idobj || !deleteCheck || deleteCheck.hasInvoice || isDeleteCheckLoading) return
    setIsDeleting(true)
    setDeleteErrorMessage('')
    try {
      const result = await deleteObjednavka(selectedOrder.idobj)
      fetchData()
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now(),
          message: result?.deletedStockMove
            ? `Objednávka a skladový pohyb ${result.deletedStockMove.cisloPoh || ''} byly smazány.`
            : 'Objednávka byla smazána.',
          time: new Date().toLocaleTimeString(),
        }
      ])
      closeDeleteModal()
    } catch (error) {
      console.error('Chyba při mazání objednávky:', error)
      setIsDeleting(false)
      setDeleteErrorMessage(error?.message || 'Objednávku se nepodařilo smazat.')
    }
  }

  // Filtr offcanvas
  const toggleOffcanvas = () => {
    setIsFiltrOpen((prevState) => !prevState)    
  }
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Zavřít pouze pokud je offcanvas otevřený a klik nebyl na element v offcanvas a na tlačítka offcanvas
      if (isFiltrOpen && !event.target.closest('.offcanvas-custom') && !event.target.closest('#offcanvas-custom-btn') && !event.target.closest('.filter-active-item') && !event.target.closest('.btn-clear-filters')) {
        setIsFiltrOpen(false) 
      }
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isFiltrOpen) {
        setIsFiltrOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isFiltrOpen])

  // Parsování vstupního výrazu pro filtrování datumu pořízení
  const parseDateFilter = (input, setIsDateInvalid) => {
    const filter = { datum1: '', datum2: '', datumOperator: '' }
    // Regulární výraz pro datum ve formátu dd.mm.yyyy
    const dateRegex = /^\d{1,2}\.\d{1,2}\.\d{4}$/
    if (input.includes('..')) {
      // Rozsah: 1.1.2024..31.12.2024
      const [datum1, datum2] = input.split('..').map(d => d.trim())
      if (dateRegex.test(datum1) && dateRegex.test(datum2)) {
        filter.datum1 = datum1
        filter.datum2 = datum2
        filter.datumOperator = '..'
      }
    } else if (/^(<|>|<=|>=)/.test(input)) {
      // Porovnání: <1.1.2024, >1.1.2024, <=1.1.2024, >=1.1.2024
      const match = input.match(/^(<|>|<=|>=)(\d{1,2}\.\d{1,2}\.\d{4})$/)
      if (match && dateRegex.test(match[2])) {
        filter.datumOperator = match[1]
        filter.datum1 = match[2]
      }
    } else if (dateRegex.test(input)) {
      // Přesná hodnota: 1.1.2024
      filter.datumOperator = '='
      filter.datum1 = input
    }
    // Pokud je filter prázdný -> nevalidní vstup
    if (input !== '' && !filter.datum1 && !filter.datum2 && !filter.datumOperator) {
      setIsDateInvalid(true)
    } else {
      setIsDateInvalid(false)
    }
    return filter
  }

  // Parsování vstupního výrazu pro filtrování ceny
  const parsePriceFilter = (input) => {
    const filter = { cena1: '', cena2: '', cenaOperator: '' }
    // Nahrazení čárek tečkami pro správné parsování desetinných čísel
    const sanitizedInput = input.replace(',', '.')
    if (sanitizedInput.includes('..')) {
      // Rozsah: 100..200
      const [cena1, cena2] = sanitizedInput.split('..').map(Number)
      filter.cena1 = cena1
      filter.cena2 = cena2
      filter.cenaOperator = '..'
    } else if (/^(<|>|<=|>=)/.test(sanitizedInput)) {
      // Porovnání: <100, >100, <=100, >=100
      const match = sanitizedInput.match(/^(<|>|<=|>=)(\d+(\.\d+)?)$/)
      if (match) {
        filter.cenaOperator = match[1]
        filter.cena1 = Number(match[2])
      }
    } else if (/^\d+(\.\d+)?$/.test(sanitizedInput)) {
      // Přesná hodnota: 100
      filter.cenaOperator = '='
      filter.cena1 = Number(sanitizedInput)
    }
    // Pokud je filter prázdný -> nevalidní vstup
    if (input !== '' && !filter.cena1 && !filter.cena2 && !filter.cenaOperator) {
      setIsPriceInvalid(true)
    } else {
      setIsPriceInvalid(false)
    }
    return filter
  } 

  // Parsování vstupního výrazu pro filtrování čísla objednávky
  const parseOrderFilter = (input) => {
    const filter = { cislo: '', cisloOperator: '' }
    if (input.endsWith('*')) {
      // Hledání podle prefixu
      filter.cisloOperator = 'startsWith'
      filter.cislo = input.slice(0, -1) // odstraníme *
    } else if (input !== '') {
      // Přesná shoda
      filter.cisloOperator = '='
      filter.cislo = input
    }
    return filter
  }

  const parseTextIdentifierFilter = (field, input) => {
    const value = String(input ?? '').trim()
    return {
      [field]: value.endsWith('*') ? value.slice(0, -1) : value,
      [`${field}Operator`]: value ? (value.endsWith('*') ? 'startsWith' : '=') : '',
    }
  }

  // Parsování vstupního výrazu pro filtrování ič
  const parseIcoFilter = (input) => {
    const filter = { ico: '', icoOperator: '' }
    if (input.endsWith('*')) {
      // Hledání podle prefixu
      filter.icoOperator = 'startsWith'
      filter.ico = input.slice(0, -1) // odstraníme *
    } else if (input !== '') {
      // Přesná shoda
      filter.icoOperator = '='
      filter.ico = input
    }
    return filter
  }

  // Parsování vstupního výrazu pro filtrování zákazníka
  const parseZakaznikFilter = (input) => {
    const filter = { zakaznik: '', zakaznikOperator: '' }
    if (input.endsWith('*')) {
      // Hledání podle prefixu
      filter.zakaznikOperator = 'startsWith'
      filter.zakaznik = input.slice(0, -1) // odstraníme *
    } else if (input !== '') {
      // Přesná shoda
      filter.zakaznikOperator = '='
      filter.zakaznik = input
    }
    return filter
  }

  // helper pro dropdown rychlé filtry
    const formatDateCZ = (date) => {
    const d = String(date.getDate()).padStart(2, "0")
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const y = date.getFullYear()
    return `${d}.${m}.${y}`
  }

  // preset = kompletní reset + nastavení jen toho, co má preset řešit
  const applyPreset = (presetFn) => {
    const datumString = formatDateCZ(new Date())
    const parsedDate = parseDateFilter(datumString, setIsDateInvalid)

    // reset všeho (filtry + inputy)
    setFiltr({})
    setDateInput("")
    setIsDateInvalid(false)
    setPriceInput("")
    setIsPriceInvalid(false)
    setCisloInput("")
    setIcoInput("")
    setZakaznikInput("")
    setSpzInput("")
    setCisloPohInput("")
    setOrigcisloInput("")
    setZakazkaInput("")

    // aplikuj preset
    const preset = presetFn({ datumString, parsedDate })

    setFiltr(preset)

    // u presetů s datem chceme i zobrazit datum v inputu
    if (preset.datum1) setDateInput(datumString)
  }

  // presety 
  const presetTodayUnfinished = ({ parsedDate }) => ({
    ...parsedDate,
    stav_id: 2,
    stav_nazev: "nevyřízeno",
  })

  const presetTodayAll = ({ parsedDate }) => ({
    ...parsedDate,
  })

  const presetAllPrijato = () => ({
    stav_id: 2,
    stav_key: '2|prijato',
    stav_nazev: 'přijato',
  })

  const presetAllDilna = () => ({
    stav_id: 2,
    stav_key: '2|dilna',
    stav_nazev: 'dílna',
  })

  const presetAllProvedeno = () => ({
    stav_id: 2,
    stav_key: '2|provedeno',
    stav_nazev: 'provedeno',
  })

  const renderIdentifierFilter = ({ controlId, label, field, value, setValue }) => (
    <Col xs={12}>
      <Form.Group controlId={controlId}>
        <FloatingLabel label={label}>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`${controlId}-tooltip`}>
                pro potvrzení stiskni Enter<br />
                povolený zápis s *
              </Tooltip>
            }
          >
            <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
          </OverlayTrigger>
          <Form.Control
            type="text"
            name={controlId}
            className={filtr[field] ? 'border-primary border-opacity-25' : ''}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => setFiltr((current) => ({
              ...current,
              ...parseTextIdentifierFilter(field, e.target.value),
            }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setFiltr((current) => ({
                  ...current,
                  ...parseTextIdentifierFilter(field, e.target.value),
                }))
              }
            }}
          />
        </FloatingLabel>
      </Form.Group>
    </Col>
  )

  return (
    <>
      {/* Filtr wrapper */}
      {(filtr?.uzivatel ||
        filtr?.cena1 ||
        filtr?.datum1 ||
        filtr?.cislo ||
        filtr?.cisloPoh ||
        filtr?.origcislo ||
        filtr?.zakazka ||
        filtr?.ico ||
        filtr?.zakaznik ||
        filtr?.spz ||
        filtr?.stav_id !== undefined
      ) && (
        <h6>Aktuální filtr</h6>
      )}
      <div className='filter-active-wrapper'> 
        {filtr?.datum1 && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Datum</small></span>
            <button 
              type='button'
              title="Zrušit filtr" 
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { datum1, datum2, datumOperator, ...rest } = prevFiltr 
                  return rest
                })
                setDateInput('')
              }}
            >{dateInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}  
        {filtr?.cislo && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Číslo</small></span>
            <button 
              type='button'
              title="Zrušit filtr"
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { cislo, cisloOperator, ...rest } = prevFiltr 
                  return rest
                })
                setCisloInput('')
              }} 
            >
              {cisloInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr?.cisloPoh && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Č. skladového pohybu</small></span>
            <button
              type='button'
              title="Zrušit filtr"
              className='btn btn-outline-primary btn-sm'
              onClick={() => {
                setFiltr(({ cisloPoh, cisloPohOperator, ...rest }) => rest)
                setCisloPohInput('')
              }}
            >
              {cisloPohInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr?.origcislo && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Pův. č. obj.</small></span>
            <button
              type='button'
              title="Zrušit filtr"
              className='btn btn-outline-primary btn-sm'
              onClick={() => {
                setFiltr(({ origcislo, origcisloOperator, ...rest }) => rest)
                setOrigcisloInput('')
              }}
            >
              {origcisloInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr?.zakazka && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Č. zakázky</small></span>
            <button
              type='button'
              title="Zrušit filtr"
              className='btn btn-outline-primary btn-sm'
              onClick={() => {
                setFiltr(({ zakazka, zakazkaOperator, ...rest }) => rest)
                setZakazkaInput('')
              }}
            >
              {zakazkaInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr?.ico && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>IČ</small></span>
            <button 
              type='button'
              title="Zrušit filtr"
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { ico, icoOperator, ...rest } = prevFiltr 
                  return rest
                })
                setIcoInput('')
              }} 
            >
              {icoInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )} 
        {filtr?.zakaznik && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Zákazník</small></span>
            <button 
              type='button'
              title="Zrušit filtr"
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { zakaznik, zakaznikOperator, ...rest } = prevFiltr 
                  return rest
                })
                setZakaznikInput('')
              }} 
            >
              {zakaznikInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}  
        {filtr?.spz && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>SPZ</small></span>
            <button 
              type='button'
              title="Zrušit filtr" 
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { spz, ...rest } = prevFiltr 
                  return rest
                })
                setSpzInput('')
              }}
            >{filtr.spz} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr?.cena1 && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Částka</small></span>
            <button 
              type='button'
              title="Zrušit filtr"
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { cena1, cena2, cena_operator, ...rest } = prevFiltr 
                  return rest
                })
                setPriceInput('')
              }} 
            >
              {priceInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )} 
        {filtr?.uzivatel && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Vytvořil</small></span>
            <button 
              type='button'
              title="Zrušit filtr" 
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { uzivatel, uzivatel_label, ...rest } = prevFiltr 
                  return rest
                })
              }}
            >{filtr.uzivatel_label} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )} 
        {(filtr?.stav_key || filtr?.stav_id !== undefined) && (
          <div className='filter-active-item'>
            <span className='text-muted ms-1'><small>Stav</small></span>
            <button 
              type='button'
              title="Zrušit filtr" 
              className='btn btn-outline-primary btn-sm'
              onClick={() => { 
                setFiltr(prevFiltr => {
                  const { stav_id, stav_key, stav_nazev, ...rest } = prevFiltr 
                  return rest
                })
              }}
            >{filtr.stav_nazev} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr?.uzivatel ||
            filtr?.cena1 ||
            filtr?.datum1 ||
            filtr?.cislo ||
            filtr?.cisloPoh ||
            filtr?.origcislo ||
            filtr?.zakazka ||
            filtr?.ico ||
            filtr?.zakaznik ||
            filtr?.spz ||
            filtr?.stav_id !== undefined ||
            filtr?.stav_key
          ) && (
          <button 
          title="Zrušit všechny filtry" 
          onClick={() => {
            setFiltr({})
            setDateInput('')
            setIsDateInvalid(false)
            setPriceInput('')
            setIsPriceInvalid(false)
            setCisloInput('')
            setIcoInput('')
            setZakaznikInput('')
            setSpzInput('')
            setCisloPohInput('')
            setOrigcisloInput('')
            setZakazkaInput('')
          }}
          className='btn-clear-filters'
        >
          <i className="bi bi-trash"></i>
        </button>
        )}
      </div>

      {/* Header */}
      <div className="row d-flex align-items-center justify-content-space my-2">
        <div className='col'>
          <button 
            className="btn btn-primary me-1" 
            title='Nový záznam'
            onClick={() => handleCreate()} 
          >
            <i className="bi bi-plus-circle me-2"></i>Přidat
          </button>
          <button 
            id='offcanvas-custom-btn'
            className="btn btn-outline-primary me-1" 
            title='Filtr zakázek'
            onClick={() => toggleOffcanvas()} 
          >
            <i className="bi bi-sliders me-2"></i>Filtr
          </button>
          <Dropdown className="d-inline">
            <Dropdown.Toggle
              variant="outline-primary"
              id="orders-quick-filters"
              title="Rychlé filtry"
            >
              <i className="bi bi-clock-history me-2"></i>Rychlé filtry
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => applyPreset(presetTodayUnfinished)}>
                <i className="bi bi-clock me-2"></i>Dnes nevyřízené
              </Dropdown.Item>
              <Dropdown.Item onClick={() => applyPreset(presetTodayAll)}>
                <i className="bi bi-calendar-day me-2"></i>Dnes vše
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => applyPreset(presetAllPrijato)}>
                <i className="bi bi-clipboard-check me-2"></i>Vše přijato
              </Dropdown.Item>
              <Dropdown.Item onClick={() => applyPreset(presetAllDilna)}>
                <i className="bi bi-wrench me-2"></i>Vše dílna
              </Dropdown.Item>
              <Dropdown.Item onClick={() => applyPreset(presetAllProvedeno)}>
                <i className="bi bi-hourglass-split me-2"></i>Vše provedeno
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className='col'>
            <div className="search-wrapper">
            <span className="search-icon"><i className="bi bi-search"></i></span>
            <input
              type="text"
              placeholder="Hledat..."
              value={table.searchQuery}
              onChange={(e) => table.setSearchQuery(e.target.value)}
            />
            {table.searchQuery && (            
              <span 
                className="clear-icon" 
                onClick={() => table.setSearchQuery('')}
              >
                <i className="bi bi-x"></i>
              </span>        
            )}
          </div> 
        </div>            
      </div> 

      {/* Filtr offcanvas */}
      <div className={`offcanvas-custom ${isFiltrOpen ? 'open' : ''}`}>
        <div className='d-flex justify-content-between align-items-center p-4 mb-3'>
          <h4 className='my-1'>Filtrování zakázek</h4>
          <div className=''>
            <button type="button" className="btn" title='Zrušit vše' onClick={() => {
              setFiltr({})
              setDateInput('')
              setIsDateInvalid(false)
              setPriceInput('')
              setIsPriceInvalid(false)
              setCisloInput('')
              setIcoInput('')
              setZakaznikInput('')
              setSpzInput('')
              setCisloPohInput('')
              setOrigcisloInput('')
              setZakazkaInput('')
            }}><i className="bi bi-trash"></i></button>
            <button type="button" className="btn" aria-label="Close" title='Zavřít' onClick={toggleOffcanvas}><i className="bi bi-x-lg"></i></button>
          </div>
        </div>
        <Container className='px-4'>
          <Row className='g-2'>  
            <Col xs={12}>
              <Form.Group controlId="offcanvasDatum">
                <FloatingLabel label={'datum pořízení'}>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-datum">
                        pro potvrzení stiskni Enter<br/>
                        povolené operace &lt;, &gt;, &lt;=, &gt;= <br />
                        pro rozsah od-do použij ..                         
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger>                  
                  <Form.Control
                    type="text"
                    name="filtrDatum"
                    className={`
                      ${(filtr.datum1 || filtr.datum2) ? 'border-primary border-opacity-25' : ''}
                      ${isDateInvalid ? 'is-invalid' : ''}
                    `}
                    value={dateInput}
                    onChange={(e) => setDateInput(e.target.value)}
                    onBlur={(e) => {
                      const parsedFilter = parseDateFilter(e.target.value, setIsDateInvalid)
                      setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const parsedFilter = parseDateFilter(e.target.value, setIsDateInvalid)
                        setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    neplatný formát datumu
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col> 
            <Col xs={12}>
              <Form.Group controlId="offcanvasCislo">
                <FloatingLabel label={'číslo objednávky'}>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-cislo">
                        pro potvrzení stiskni Enter<br/>
                        povolený zápis s *
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger>                  
                  <Form.Control
                    type="text"
                    name="filtrCislo"
                    className={`
                      ${(filtr.cislo) ? 'border-primary border-opacity-25' : ''}
                    `}
                    value={cisloInput}
                    onChange={(e) => setCisloInput(e.target.value)}
                    onBlur={(e) => {
                      const parsedFilter = parseOrderFilter(e.target.value)
                      setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const parsedFilter = parseOrderFilter(e.target.value)
                        setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                      }
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            {renderIdentifierFilter({
              controlId: 'filtrCisloPoh',
              label: 'číslo skladového pohybu',
              field: 'cisloPoh',
              value: cisloPohInput,
              setValue: setCisloPohInput,
            })}
            {renderIdentifierFilter({
              controlId: 'filtrOrigcislo',
              label: 'pův. číslo objednávky',
              field: 'origcislo',
              value: origcisloInput,
              setValue: setOrigcisloInput,
            })}
            {renderIdentifierFilter({
              controlId: 'filtrZakazka',
              label: 'číslo zakázky',
              field: 'zakazka',
              value: zakazkaInput,
              setValue: setZakazkaInput,
            })}
            <Col xs={12}>
              <Form.Group controlId="offcanvasIco">
                <FloatingLabel label={'ič'}>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-ico">
                        pro potvrzení stiskni Enter<br/>
                        povolený zápis s *
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger>                  
                  <Form.Control
                    type="text"
                    name="filtrIco"
                    className={`
                      ${(filtr.ico) ? 'border-primary border-opacity-25' : ''}
                    `}
                    value={icoInput}
                    onChange={(e) => setIcoInput(e.target.value)}
                    onBlur={(e) => {
                      const parsedFilter = parseIcoFilter(e.target.value)
                      setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const parsedFilter = parseIcoFilter(e.target.value)
                        setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                      }
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="offcanvasZakaznik">
                <FloatingLabel label={'zákazník'}>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-firma">
                        pro potvrzení stiskni Enter<br/>
                        povolený zápis s *
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger>                  
                  <Form.Control
                    type="text"
                    name="filtrZakaznik"
                    className={`
                      ${(filtr.zakaznik) ? 'border-primary border-opacity-25' : ''}
                    `}
                    value={zakaznikInput}
                    onChange={(e) => setZakaznikInput(e.target.value)}
                    onBlur={(e) => {
                      const parsedFilter = parseZakaznikFilter(e.target.value)
                      setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const parsedFilter = parseZakaznikFilter(e.target.value)
                        setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                      }
                    }}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>            
            <Col xs={12}>
              <Form.Group controlId="offcanvasSpz">
                <FloatingLabel label={'spz'}>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-firma">
                        pro potvrzení stiskni Enter<br/>
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger>   
                  <Form.Control
                    type="text"
                    name="filtrSpz"
                    className={filtr.spz ? 'border-primary border-opacity-25' : ''}
                    value={spzInput}
                    onChange={(e) => setSpzInput(e.target.value)}
                    onBlur={(e) => {
                      const selectedId = e.target.value
                      setFiltr(s => ({ ...s, 'spz': selectedId }))
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const selectedId = e.target.value
                        setFiltr(s => ({ ...s, 'spz': selectedId }))
                      }
                    }}                    
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>            
            <Col xs={12}>
              <Form.Group controlId="offcanvasCelkem">
                <FloatingLabel label={'celková částka'}>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-cena">
                        pro potvrzení stiskni Enter<br/>
                        povolené operace &lt;, &gt;, &lt;=, &gt;= <br />
                        pro rozsah od-do použij ..                         
                      </Tooltip>
                    }
                  >
                    <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                  </OverlayTrigger>                  
                  <Form.Control
                    type="text"
                    name="filtrCelkem"
                    className={`
                      ${(filtr.cena1 || filtr.cena2) ? 'border-primary border-opacity-25' : ''}
                      ${isPriceInvalid ? 'is-invalid' : ''}
                    `}
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    onBlur={(e) => {
                      const parsedFilter = parsePriceFilter(e.target.value, setIsPriceInvalid)
                      setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const parsedFilter = parsePriceFilter(e.target.value, setIsPriceInvalid)
                        setFiltr(prevFiltr => ({ ...prevFiltr, ...parsedFilter }))
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    neplatný formát částky
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="offcanvasUzivatel">
                <FloatingLabel label={'vytvořil'}>                    
                  <Form.Select
                    name="filtrUzivatel"
                    className={filtr.uzivatel ? "border-primary border-opacity-25" : ""}
                    value={filtr?.uzivatel || ""}
                    onChange={(e) => {
                      const val = e.target.value
                      setFiltr((s) => ({ ...s, uzivatel: val, uzivatel_label: val }))
                    }}
                  >
                    <option value="">vyberte uživatele (MRP)</option>
                    {uzivatele.map(({ id, jmeno }) => (
                      <option key={id} value={id}>
                        {jmeno}
                      </option>
                    ))}
                  </Form.Select>             
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="offcanvasStav">
                <FloatingLabel label={'stav objednávky'}>                    
                  <Form.Select
                    name="filtrStav"
                    className={(filtr?.stav_key || filtr?.stav_id !== undefined) ? "border-primary border-opacity-25" : ""}
                    value={filtr?.stav_key ?? filtr?.stav_id ?? ""}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setFiltr((s) => {
                          const { stav_id, stav_key, stav_nazev, ...rest } = s
                          return rest
                        })
                        return
                      }

                      const selectedKey = e.target.value
                      const [codeStr, subcodeRaw] = selectedKey.split('|')
                      const selectedId = Number(codeStr)
                      const selectedSub = subcodeRaw || null
                      const selected = ORDER_STATUSES.find(
                        (s) => s.code === selectedId && (s.subcode ?? null) === selectedSub
                      )
                      setFiltr((s) => ({
                        ...s,
                        stav_id: selectedId,
                        stav_key: selectedKey,
                        stav_nazev: selected?.popis || ""
                      }))
                    }}
                  >
                    <option value="">vyberte stav objednávky</option>
                    {ORDER_STATUSES.map(({ code, subcode, popis }) => (
                      <option key={`${code}|${subcode ?? ''}`} value={`${code}|${subcode ?? ''}`}>
                        {popis}
                      </option>
                    ))}
                  </Form.Select>              
                </FloatingLabel>
              </Form.Group>
            </Col>            
          </Row> 
        </Container>        
      </div>

      {/* Tabulka */}
      <div className='row my-3'>
        <div className='col'>
          {isLoading && (
            <div className="d-flex align-items-center text-muted small mb-2">
              <Spinner animation="border" size="sm" className="me-2" />
              Načítám objednávky…
            </div>
          )}
          <Table responsive hover className='table-transparent'>
            <thead>
              <tr>                
                <th className='sort' onClick={() => table.handleSort('vytvoreno')}>datum <i className={`bi ${table.sortField === 'vytvoreno' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('cislo')}>objednávka <i className={`bi ${table.sortField === 'cislo' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th> 
                <th onClick={() => table.handleSort('cisloPoh')}>pohyb <i className={`bi ${table.sortField === 'cisloPoh' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('origcislo')}>pův.č.obj. <i className={`bi ${table.sortField === 'origcislo' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('zakazka')}>zakázka <i className={`bi ${table.sortField === 'zakazka' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('ico')}>ič <i className={`bi ${table.sortField === 'ico' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>               
                <th onClick={() => table.handleSort('zakaznik')}>zákazník <i className={`bi ${table.sortField === 'zakaznik' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('spz')}>spz <i className={`bi ${table.sortField === 'spz' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('celkem')}>celkem <i className={`bi ${table.sortField === 'celkem' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>    
                <th className='sort' onClick={() => table.handleSort('uzivatel')}>
                  uživatel{" "}
                  <i
                    className={`bi ${
                      table.sortField === "uzivatel"
                        ? table.sortDirection === "asc"
                          ? "bi-caret-up-fill"
                          : "bi-caret-down-fill"
                        : "invisible-icon"
                    }`}
                  ></i>
                </th>
                <th style={{ width: '7%', textAlign: 'center' }} onClick={() => table.handleSort('stav_sort')}>stav <i className={`bi ${table.sortField === 'stav_sort' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th> 
                <th style={{ width: '5%' }}>akce</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr className='no-data-row'>
                  <td colSpan="12" className='border-0'>
                    <div className='d-flex flex-column align-items-center justify-content-center my-3 text-muted small'>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Načítám objednávky…
                    </div>
                  </td>
                </tr>
              ) : table.data.length > 0 ? (
                table.data.map((order) => {
                  const { idobj, cislo, cisloPoh, origcislo, zakazka, ico, zakaznik, spz, celkem, vytvoreno, uzivatel, stav_bg, stav_icon, stav_color, stav_label } = order
                  const isStockMoveDisabled = Number(order?.stav) === 0 || Number(order?.stav) === 1
                  const stockMoveBlockedByStatus = ['prijato', 'dilna'].includes(normalizeSubcode(order?.stav_label))
                  return (
                  <tr 
                    key={idobj}
                    onDoubleClick={() => handleUpdate(order)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: stav_bg || "",
                    }}
                  >
                    <td>{vytvoreno ? new Date(vytvoreno).toLocaleDateString('cs-CZ', { year: 'numeric', month: '2-digit', day: '2-digit' }) : ''}</td>
                    <td>{cislo}</td>
                    <td>{cisloPoh}</td>
                    <td>{origcislo}</td>
                    <td>{zakazka}</td>
                    <td>{ico}</td>
                    <td>{zakaznik ? (zakaznik.length > 50 ? `${zakaznik.slice(0, 40)}...` : zakaznik) : ''}</td>
                    <td>{spz}</td>
                    <td>{Number(celkem).toLocaleString('cs-CZ', { style: 'currency', currency: 'CZK' })}</td>                    
                    <td>{uzivatel}</td>
                    <td style={{ width: '7%', textAlign: 'center' }}>
                      {stav_icon && (
                        <span title={stav_label}>
                          <i className={`bi ${stav_icon} ${stav_color}`}></i>
                        </span>
                      )}
                    </td>
                    <td style={{ width: '5%' }}>
                      <Dropdown
                        align="end"
                        show={openActionsDropdownId === order.idobj}
                        onToggle={(nextShow) => setOpenActionsDropdownId(nextShow ? order.idobj : null)}
                        onClick={(e) => e.stopPropagation()}
                        className="d-inline-block"
                      >
                        <Dropdown.Toggle className="btn-table px-3 btn-table-no-caret" title="Akce">
                          <i className="bi bi-three-dots"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          className="px-3 py-2 rounded-2"
                          style={{ minWidth: 185 }}
                          renderOnMount
                          popperConfig={{
                            strategy: "fixed",
                            modifiers: [
                              {
                                name: "computeStyles",
                                options: {
                                  adaptive: false,
                                },
                              },
                              {
                                name: "flip",
                                options: {
                                  fallbackPlacements: ["top-end", "top-start", "bottom-end"],
                                },
                              },
                              {
                                name: "preventOverflow",
                                options: {
                                  boundary: "viewport",
                                  padding: 8,
                                },
                              },
                            ],
                          }}
                        >
                          <div className="p-0 py-1 small text-muted">Akce</div>
                          <Dropdown.Item
                            as="button"
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleUpdate(order) }}
                            className="p-0 py-1 d-flex align-items-center gap-2"
                          >
                            <i className="bi bi-pencil-square"></i>
                            <span className="small text-muted">Upravit objednávku</span>
                          </Dropdown.Item>    
                          <Dropdown.Item
                            as="button"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              openDeleteModal(order)
                              closeParentDropdown(e.currentTarget)
                            }}
                            className="p-0 py-1 d-flex align-items-center gap-2"
                          >
                            <i className="bi bi-trash"></i>
                            <span className="small text-muted">Smazat objednávku</span>
                          </Dropdown.Item> 
                          <Dropdown.Item
                            as="button"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (isStockMoveDisabled) {
                                setNotifications((prev) => [
                                  ...prev,
                                  {
                                    id: Date.now(),
                                    type: 'error',
                                    message: 'Skladový pohyb byl již vytvořen.',
                                    time: new Date().toLocaleTimeString(),
                                  }
                                ])
                                closeParentDropdown(e.currentTarget)
                                return
                              }
                              if (stockMoveBlockedByStatus) {
                                setNotifications((prev) => [
                                  ...prev,
                                  {
                                    id: Date.now(),
                                    type: 'error',
                                    message: 'Nejdříve je nutné objednávku dokončit a uzavřít.',
                                    time: new Date().toLocaleTimeString(),
                                  }
                                ])
                                closeParentDropdown(e.currentTarget)
                                return
                              }
                              openStockMoveModal(order)
                              closeParentDropdown(e.currentTarget)
                            }}
                            style={{ cursor: 'pointer' }}
                            className="p-0 py-1 d-flex align-items-center gap-2"
                          >
                            <i className="bi bi-file-earmark-plus"></i>
                            <span className="small text-muted">Přidat skladový pohyb</span>
                          </Dropdown.Item>
                          <Dropdown.Divider className="my-1" />
                          <div className="p-0 py-1 small text-muted">Tisk</div>

                          <OrdersPrintTrigger
                            order={order}
                            onError={handleOrderPrintError}
                            renderButton={({ onClick, disabled }) => (
                              <Dropdown.Item
                                as="button"
                                type="button"
                                onClick={(e) => {
                                  onClick(e)
                                  closeParentDropdown(e.currentTarget)
                                }}
                                disabled={disabled}
                                className="p-0 py-1 d-flex align-items-center gap-2"
                              >
                                <i className="bi bi-journal-text"></i>
                                <span className="small text-muted">Zakázkový list</span>
                              </Dropdown.Item>
                            )}
                          />
                          <OrdersSalePrintTrigger
                            order={order}
                            onError={handleOrderPrintError}
                            renderButton={({ onClick, disabled }) => (
                              <Dropdown.Item
                                as="button"
                                type="button"
                                onClick={(e) => {
                                  onClick(e)
                                  closeParentDropdown(e.currentTarget)
                                }}
                                disabled={disabled}
                                className="p-0 py-1 d-flex align-items-center gap-2"
                              >
                                <i className="bi bi-journal-richtext"></i>
                                <span className="small text-muted">Prodejka</span>
                              </Dropdown.Item>
                            )}
                          />
                          <OrdersDeliveryPrintTrigger
                            order={order}
                            onError={handleOrderPrintError}
                            renderButton={({ onClick, disabled }) => (
                              <Dropdown.Item
                                as="button"
                                type="button"
                                onClick={(e) => {
                                  onClick(e)
                                  closeParentDropdown(e.currentTarget)
                                }}
                                disabled={disabled}
                                className="p-0 py-1 d-flex align-items-center gap-2"
                              >
                                <i className="bi bi-journal-medical"></i>
                                <span className="small text-muted">Dodací list</span>
                              </Dropdown.Item>
                            )}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                )})
              ) : hasLoaded ? (
                <tr className='no-data-row'>
                  <td colSpan="12" className='border-0'>
                    <div className='d-block my-3 text-center'>
                      <i className="bi bi-journal-text fs-4"></i>
                      <p className='small'>Nebyly nalezeny žádné údaje.</p>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Stránkování */}
      <div className="row d-flex align-items-center justify-content-between my-2">
        <div className="col-auto d-flex flex-wrap align-items-center gap-1 small">
          {ORDER_STATUSES.map((status) => (
            <span
              key={`${status.code}|${status.subcode ?? ''}`}
              className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded"
              style={{ backgroundColor: status.bgColor || 'transparent' }}
              title={status.popis}
            >
              <i className={`bi ${status.icon} ${status.color}`}></i>
              <span>{status.popis}</span>
            </span>
          ))}
        </div>
        <div className="col pagination small text-end">
          {table.totalItems > 0 && (
            <>
            <span>{table.startIndex + 1} – {Math.min(table.endIndex, table.totalItems)} z {table.totalItems}</span>
            <button className="btn btn-sm border-0 p-0" onClick={table.handlePrevPage} disabled={table.currentPage === 1}><i className="bi bi-chevron-left"></i></button>
            <button className="btn btn-sm border-0 p-0" onClick={table.handleNextPage} disabled={table.currentPage === table.totalPages}><i className="bi bi-chevron-right"></i></button>
            <select value={table.itemsPerPage} onChange={(e) => table.setItemsPerPage(Number(e.target.value))}>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            </>
          )}
        </div>
      </div>

      {/* Modal - smazání objednávky */}
      {isDeleteModalOpen && selectedOrder && (
        <Modal show={isDeleteModalOpen} fullscreen="xl-down" onHide={closeDeleteModal} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered>
          <Modal.Header closeButton className='border-0 px-4'>
            <Modal.Title className='text-primary mt-3 lh-1'>Objednávka<br/>
              <span className='text-muted fs-6'>odstranit záznam</span>
            </Modal.Title>            
          </Modal.Header>
          <Form onSubmit={handleDeleteOrder}>
            <Modal.Body className='px-4'>
              <Row className='g-1'>
                {deleteErrorMessage && (
                  <Col xs={12}>
                    <Alert variant="danger" className="d-flex align-items-center mb-2 small">
                      <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                      <span>{deleteErrorMessage}</span>
                    </Alert>
                  </Col>
                )}
                <Col xs={12}>
                  <Form.Group controlId="deleteCislo">
                    <FloatingLabel label='číslo'>
                      <Form.Control
                        type="text"
                        value={selectedOrder.cislo || ''}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="deleteZakaznik">
                    <FloatingLabel label='zákazník'>
                      <Form.Control
                        type="text"
                        value={selectedOrder.zakaznik || ''}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="deleteStredisko">
                    <FloatingLabel label='středisko'>
                      <Form.Control
                        type="text"
                        value={selectedOrder.stredisko || ''}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="deleteDatum">
                    <FloatingLabel label='datum'>
                      <Form.Control
                        type="text"
                        value={selectedOrder.vytvoreno ? new Date(selectedOrder.vytvoreno).toLocaleDateString('cs-CZ', { year: 'numeric', month: '2-digit', day: '2-digit' }) : ''}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  {isDeleteCheckLoading ? (
                    <Alert variant="info" className="d-flex align-items-center mt-2 mb-0 small">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Ověřuji návaznosti objednávky…
                    </Alert>
                  ) : deleteCheck?.hasInvoice ? (
                    <Alert variant="danger" className="d-flex align-items-center mt-2 mb-0 small">
                      <i className="bi bi-x-octagon pe-3 fs-3"></i>
                      <span>Objednávku nelze smazat. Na objednávku je navázán skladový pohyb {deleteCheck.cisloPoh || ''} a faktura {deleteCheck.cisloFaktury || ''}.</span>
                    </Alert>
                  ) : deleteCheck?.hasStockMove ? (
                    <Alert variant="danger" className="d-flex align-items-center mt-2 mb-0 small">
                      <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                      <span>Na objednávku je navázán skladový pohyb {deleteCheck.cisloPoh || ''}. Pokud byl doklad uhrazen, nebude souhlasit stav pokladny. Objednávka i skladový pohyb budou nevratně smazány.</span>
                    </Alert>
                  ) : deleteCheck ? (
                    <Alert variant="warning" className="d-flex align-items-center mt-2 mb-0 small">
                      <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                      <span>Skladový pohyb nebyl nalezen. Objednávka bude nevratně odstraněna. Navázaná rezervace v kalendáři zůstane zachována a vrátí se do stavu „Přijato“.</span>
                    </Alert>
                  ) : null}
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className='border-0 d-flex justify-content-start px-4 pb-3'>
              <Button variant="primary mx-0" type="submit" disabled={isDeleting || isDeleteCheckLoading || !deleteCheck || deleteCheck.hasInvoice}>
                {isDeleting ? 'Mazání...' : 'Smazat'}
              </Button>
              <Button variant="secondary ms-1" onClick={closeDeleteModal} disabled={isDeleting}>Zavřít</Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {isStockMoveModalOpen && selectedOrder && (
        <Modal show={isStockMoveModalOpen} fullscreen="xl-down" onHide={closeStockMoveModal} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered>
          <Modal.Header closeButton className='border-0 px-4'>
            <Modal.Title className='text-primary mt-3 lh-1'>Skladový pohyb<br/>
              <span className='text-muted fs-6'>vytvořit nový skladový pohyb</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='px-4'>
            <Row className='g-2'>
              <Col xs={12}>
                <Form.Group controlId="stockMoveCislo">
                  <FloatingLabel label='číslo objednávky'>
                    <Form.Control type="text" value={selectedOrder.cislo || ''} readOnly disabled />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="stockMoveZakaznik">
                  <FloatingLabel label='zákazník'>
                    <Form.Control type="text" value={selectedOrder.zakaznik || ''} readOnly disabled />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="stockMoveDatum">
                  <FloatingLabel label='datum'>
                    <Form.Control
                      type="text"
                      value={selectedOrder.vytvoreno ? new Date(selectedOrder.vytvoreno).toLocaleDateString('cs-CZ', { year: 'numeric', month: '2-digit', day: '2-digit' }) : ''}
                      readOnly
                      disabled
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="stockMoveCastka">
                  <FloatingLabel label='částka'>
                    <Form.Control
                      type="text"
                      value={Number(selectedOrder.celkem || 0).toLocaleString('cs-CZ', { style: 'currency', currency: 'CZK' })}
                      readOnly
                      disabled
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <p className="small text-muted mb-2">
                  Chcete vytvořit nový skladový pohyb a označit objednávku jako vyřízenou? Vyberte typ skladového pohybu:
                </p>
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="radio"
                  id="stockMoveTypeCash"
                  name="stockMoveType"
                  label="Prodejka za hotové"
                  checked={stockMoveType === 'cash'}
                  onChange={() => setStockMoveType('cash')}
                  disabled={isInvoiceOnlyByLaw(selectedOrder)}
                />
                <Form.Check
                  type="radio"
                  id="stockMoveTypeInvoice"
                  name="stockMoveType"
                  label="Výdej na fakturu"
                  checked={stockMoveType === 'invoice'}
                  onChange={() => setStockMoveType('invoice')}
                />
              </Col>
              {isInvoiceOnlyByLaw(selectedOrder) && (
                <Col xs={12}>
                  <Alert variant="info" className="small py-2 mb-0">
                    Podmínky objednávky dovolují vystavit pouze výdej na fakturu.
                  </Alert>
                </Col>
              )}
            </Row>
            <div className='mt-4'>
            <Button className='me-1' variant="primary" type="button" onClick={handleCreateStockMove} disabled={isCreatingStockMove}>
              {isCreatingStockMove ? 'Vytvářím...' : 'Vytvořit'}
            </Button>
            <Button className='ms-0' variant="secondary" type="button" onClick={closeStockMoveModal} disabled={isCreatingStockMove}>Zpět</Button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      <Notification notifications={notifications} setNotifications={setNotifications} />   
    </>
  )
}

export default Orders 






